import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAgentConnected } from './AgentStatus';
import { Link } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { formatDistanceToNow } from 'date-fns';
import { useAccount } from '../../context/AccountContext';
import {
  GET_READER_CHAT_MODE,
  GET_TINYCMS_DELETED_PAGES,
  GET_TINYCMS_FORM,
  GET_TINYCMS_PAGE,
  GET_TINYCMS_PAGES,
  GET_TINYCMS_SITE,
  RUN_TINYCMS_PAGE_COPY_EDIT,
} from '../../graphql';
import {
  CREATE_TINYCMS_PREVIEW,
  DELETE_TINYCMS_PAGE,
  SET_TINYCMS_HOME_PAGE,
  PUBLISH_TINYCMS_PAGES,
  RESTORE_TINYCMS_PAGE,
  SET_TINYCMS_PAGE_SHARE_IMAGE,
  UPDATE_TINYCMS_SITE,
} from '../../graphql/mutations';
import { useCopy } from '../../lib/copyToClipboard';
import { Device, DeviceSwitch, frameStyleFor, useFrameScale } from './deviceFrame';
import {
  assignPrompt,
  hidePatch,
  navIssues,
  railNote,
  removePatch,
  type NavIssue,
  type NavPart,
} from './navChecks';
import { MediaUrlPicker } from '../content/MediaUrlField';
import {
  andList,
  checkFix,
  formsPhrase,
  formsToPublish,
  otherBlocking,
  versionName,
  pageVersionLine,
} from './pageChecks';
import { useToday } from './todayData';
import { bundleBlockedLine, bundleItems, usePublishTogether } from './publishTogether';
import { RecentlyDeleted, type DeletedItem } from './RecentlyDeleted';
import { CmsNavigation } from './CmsNavigation';
import { showInkToast } from './InkToast';
import { HistoryView } from './HistoryView';
import { useSavedNumbers, withSavedNumbers } from './savedNumbers';
import { deletedSentence, homeNotice, readersSee, type HomeGap } from './homeGap';
import { SiteHomeNotice, useRestoreAllPages, useSiteHomeGap } from './SiteHomeNotice';
import {
  articleStatus,
  Finding,
  FindingsList,
  ItemState,
  PreviewButton,
  PublishPanel,
} from './shared';
import { Loader } from '../common/PageLoader';
import { useAddons } from './addons';
import { landingCampaigns } from './landingCampaigns';
import { askInk } from './InkConfirm';
import { PageCodeSection, usePageCode } from './PageCode';
import { useLiveChanges } from './useLiveChanges';
import { HomeTag } from './HomeTag';
import { madeHere } from './newPage';
import { hasAskPage } from './editorText';
import { PageEditor, usePublishEdits, type EditTarget } from './PageEditor';

/**
 * Content → Pages ("tinycms UX optimization review v5.zip", Prototype v3):
 * the site's pages and the parts on every page (header, footer) in a rail;
 * the one picked as a read-only preview of its working copy in the site's own
 * theme, desktop or mobile, with its blocks outlined on request; and, when the
 * agent has changed it, what's waiting and the way to publish it.
 *
 * Pages and the header & footer moved here from Site. The agent writes them
 * (MCP); a member reads, checks and publishes. Landing pages and forms are in
 * the rendering but not built yet.
 */

interface PageRow {
  id: string;
  path: string;
  title: string;
  workingHash: string;
  state: ItemState & { live: (ItemState['live'] & { path?: string }) | null };
  blockCount?: number;
  updatedAt?: string;
}

type Part = NavPart;

/** A page's status in the rail and its heading: colour, words, and the short tag. */
export function pageStatus(state: PageRow['state']) {
  const s = articleStatus(state);
  if (s.label === 'Taken down')
    return {
      dot: 'var(--ink-line3)',
      color: 'var(--ink-ink3)',
      label: 'Taken down',
      tag: 'Taken down',
    };
  // The person saved these edits themselves in the editor (backlog 8ffdec3d).
  if (s.tone === 'review' && state.live && (state as { ownEdit?: unknown }).ownEdit)
    return {
      dot: 'var(--ink-amber)',
      color: 'var(--ink-amber)',
      label: 'Edits not published',
      tag: 'Edited',
    };
  if (s.tone === 'review')
    return {
      dot: 'var(--ink-amber)',
      color: 'var(--ink-amber)',
      label: 'Changes pending',
      tag: 'Pending',
    };
  if (s.tone === 'draft')
    return {
      dot: 'var(--ink-line3)',
      color: 'var(--ink-ink3)',
      label: 'Not published',
      tag: 'Draft',
    };
  if (s.tone === 'scheduled')
    return {
      dot: 'var(--ink-accent)',
      color: 'var(--ink-accent)',
      label: 'Scheduled',
      tag: 'Scheduled',
    };
  return { dot: 'var(--ink-green)', color: 'var(--ink-green-ink)', label: 'Live', tag: '' };
}

export const ago = (iso?: string | null) =>
  iso ? `updated ${formatDistanceToNow(new Date(iso), { addSuffix: true })}` : '';

interface NavLink {
  label: string;
  href?: string | null;
  children?: NavLink[];
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Links counted the way a reader sees them: dropdown entries included. */
const countLinks = (links?: NavLink[] | null) =>
  (links || []).reduce((n, l) => n + (l.children?.length ? l.children.length : 1), 0);

/** All the links in the header (with its button) or in the footer (with its columns). */
const partLinkCount = (nav: any, part: Part) =>
  part === 'header'
    ? countLinks(nav?.header) + (nav?.cta ? 1 : 0)
    : countLinks(nav?.footer) +
      (nav?.footerColumns || []).reduce(
        (n: number, c: { links?: NavLink[] }) => n + (c.links?.length || 0),
        0
      );

/**
 * The Header row in the rail, listing exactly what its editor shows (446d9c6d):
 * "Site name · 5 links · button · Ask AI". The button and the Ask AI link the
 * site adds aren't counted as menu links.
 */
export const headerSummary = (nav: any, askPage: boolean) =>
  [
    'Site name',
    plural(countLinks(nav?.header), 'link'),
    nav?.cta ? 'button' : null,
    askPage && !(nav?.header || []).some((l: NavLink) => /^\/ask\/?$/.test(l.href || ''))
      ? 'Ask AI'
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * The Footer row in the rail, as its editor reads (6c406cd7): "Copyright · 2
 * columns · 9 links", all the links counted, the columns' too.
 */
export const footerSummary = (nav: any) => {
  const columns = (nav?.footerColumns || []).length;
  return [
    nav?.footerText ? 'Copyright' : null,
    columns ? plural(columns, 'column') : null,
    plural(partLinkCount(nav, 'footer'), 'link'),
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * A rail-and-preview screen (Pages v14, Forms) in grey bars, so nothing shifts
 * or reads wrong while it loads. `groups`: each rail heading and its rows' widths.
 */
export function RailSkeleton({
  label,
  groups,
}: {
  label: string;
  groups: Array<[string, number[]]>;
}) {
  const row = (key: number, width: number) => (
    <div key={key} className="pg-rail-item" aria-hidden="true" style={{ cursor: 'default' }}>
      <span className="pg-dot" style={{ background: 'var(--ink-line3)' }} />
      <span className="pg-rail-text" style={{ gap: 6 }}>
        <span className="ink-skel-bar" data-size="lg" style={{ width }} />
        <span className="ink-skel-bar" style={{ width: Math.round(width * 0.6) }} />
      </span>
    </div>
  );
  return (
    <div className="pg" role="status" aria-label={label} aria-busy="true">
      <aside className="pg-rail">
        {groups.map(([head, widths]) => (
          <div key={head}>
            <div className="pg-rail-head">{head}</div>
            {widths.map((w, i) => row(i, w))}
          </div>
        ))}
      </aside>
      <div className="pg-main" aria-hidden="true">
        <div className="pg-head">
          <div style={{ display: 'grid', gap: 10 }}>
            <span className="ink-skel-bar" style={{ width: 180, height: 22 }} />
            <span className="ink-skel-bar" style={{ width: 260 }} />
          </div>
        </div>
        <div className="pg-stage">
          <span
            className="ink-skel-bar"
            style={{ display: 'block', height: 420, borderRadius: 10 }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * `fix`: arrived from Today's Fix on `initial`, so its checks open with the open ones marked.
 * `block`: arrived from a placeholder's Edit (Approvals, new site): the preview opens there.
 */
export function CmsPagesView({
  initial,
  fix = false,
  block = null,
}: {
  initial?: string | null;
  fix?: boolean;
  block?: string | null;
}) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: siteData,
    loading: siteLoading,
    refetch: refetchSite,
  } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: deletedData,
    loading: deletedLoading,
    refetch: refetchDeleted,
  } = useQuery(GET_TINYCMS_DELETED_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const deleted: DeletedItem[] = deletedData?.tinycmsDeletedPages || [];
  const { data: chatData } = useQuery(GET_READER_CHAT_MODE, {
    variables: { accountId },
    skip: !accountId,
  });
  const pages: PageRow[] = useMemo(() => data?.tinycmsPages || [], [data]);
  const { gap } = useSiteHomeGap(accountId);
  const site = siteData?.tinycmsSite;
  const homeSlug: string | null = site?.working?.layout?.homePage || null;
  const nav = site?.working?.navigation || {};
  // Pages that exist but aren't live: a menu link to one waits for it, it isn't broken.
  const draftPaths = useMemo(
    () => new Set(pages.filter((p) => !p.state.live).map((p) => p.path.replace(/^\/+/, ''))),
    [pages]
  );
  const linkIssues = navIssues(site?.state?.findings, { draftPaths, hidden: true });

  // Landing pages say which campaign they belong to and link to it (backlog
  // bf819be4): a campaign's landing page is one of this site's pages by address.
  const { reachOn } = useAddons();
  const { data: campData } = useQuery<{
    projects: Array<{ id: string; name: string; kind: string; landingPageUrl?: string | null }>;
  }>(PAGE_CAMPAIGNS, { variables: { accountId }, skip: !accountId || !reachOn });
  const campaignOf = useMemo(
    () => landingCampaigns(campData?.projects || [], site?.siteUrl || null),
    [campData, site?.siteUrl]
  );

  // Home first, then the rest in the server's order (groups follow their parent).
  const ordered = useMemo(() => {
    const home = pages.find((p) => homeSlug && p.path === `/${homeSlug}`);
    return home ? [home, ...pages.filter((p) => p !== home)] : pages;
  }, [pages, homeSlug]);

  // Nothing picked yet means the top of the list, whatever it is when the site
  // settings arrive (the home page moves to the top then), so no default is
  // stored that could go stale.
  const [sel, setSel] = useState<string | null>(initial || null);
  // Website › Pages editing (backlog 8ffdec3d): the panel takes the rail's place.
  const [editing, setEditing] = useState<EditTarget | null>(null);
  // A page picked from outside (New page's Create draft page) is selected.
  useEffect(() => {
    if (initial) setSel(initial);
  }, [initial]);
  const onRestored = (id: string) => {
    const d = deleted.find((x) => x.id === id);
    showInkToast(
      `${d?.title || 'Untitled'} restored as a draft${d?.restoreAs ? ` at ${d.restoreAs}` : ''}.`
    );
    void refetch();
    void refetchDeleted();
    setSel(id);
  };

  // The rail needs the site settings as well as the pages: the home page and the
  // header/footer links come from them. Drawing the pages alone first selected
  // the server's first page, listed the home page under its slug and showed
  // "0 links" on both parts (UX review round 8), so hold a skeleton until both
  // are in — and, with no pages, until Recently deleted is too (it picks the
  // empty state).
  if (
    (loading && !pages.length) ||
    (siteLoading && !site) ||
    (!pages.length && deletedLoading && !deletedData)
  ) {
    return (
      <RailSkeleton
        label="Loading pages"
        groups={[
          ['SITE PAGES', [120, 96, 140, 84, 110]],
          ['ON EVERY PAGE', [70, 96]],
        ]}
      />
    );
  }
  if (!pages.length && deleted.length && accountId) {
    return <PagesGone accountId={accountId} deleted={deleted} gap={gap} onRestored={onRestored} />;
  }
  if (!pages.length) {
    return (
      <div className="pg-empty">
        <div className="pg-empty-title">No pages yet</div>
        <p>
          Ask your agent for one: “Create a pricing page”, or “Make a home page with a hero, three
          features and our latest articles”. It appears here for you to preview and publish.
        </p>
        {accountId ? (
          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <RecentlyDeleted
              accountId={accountId}
              items={deleted}
              restoreMutation={RESTORE_TINYCMS_PAGE}
              onRestored={onRestored}
              note="Restored pages come back as drafts. Publish one again to put it back on your site."
            />
          </div>
        ) : null}
      </div>
    );
  }

  const part: Part | null = sel === 'header' || sel === 'footer' ? sel : null;
  const page = part ? null : ordered.find((p) => p.id === sel) || ordered[0];
  const homeId = ordered.find((p) => homeSlug && p.path === `/${homeSlug}`)?.id;
  const editPage = (p: (typeof ordered)[number], finding?: Finding) =>
    setEditing({
      kind: 'page',
      id: p.id,
      isHome: p.id === homeId,
      landing: Boolean(campaignOf.get(p.path)),
      mine: !p.state.live && madeHere.has(p.id),
      finding,
    });

  if (editing) {
    return (
      <PageEditor
        target={editing}
        site={site}
        pages={ordered}
        onClose={() => setEditing(null)}
        onSaved={() => {
          void refetch();
          void refetchSite();
        }}
      />
    );
  }

  return (
    <>
      {gap?.homeDraft ? (
        <SiteHomeNotice
          text={homeNotice(gap).text}
          action={
            page?.id === gap.homeDraft.id
              ? null
              : { label: 'Review →', onClick: () => setSel(gap.homeDraft?.id || null) }
          }
        />
      ) : gap ? (
        <SiteHomeNotice
          text={`None of your pages are live, so visitors see ${readersSee(gap)}. Publish them to put them back on your site.`}
        />
      ) : null}
      <div className="pg">
        <aside className="pg-rail">
          <div>
            <div className="pg-rail-head">SITE PAGES</div>
            {ordered.map((p) => {
              const st = pageStatus(p.state);
              const nested = p.path.split('/').length > 2;
              const isHome = p.id === homeId;
              return (
                <button
                  key={p.id}
                  type="button"
                  className="pg-rail-item"
                  data-active={page?.id === p.id ? 'true' : undefined}
                  aria-current={page?.id === p.id ? 'page' : undefined}
                  style={nested ? { paddingLeft: 24 } : undefined}
                  onClick={() => setSel(p.id)}
                >
                  <span className="pg-dot" style={{ background: st.dot }} />
                  <span className="pg-rail-text">
                    <span className="pg-rail-title">
                      {p.title}
                      {isHome ? <HomeTag title={p.title} /> : null}
                    </span>
                    <span className="pg-rail-path">
                      {isHome ? '/' : p.path}
                      {campaignOf.get(p.path) ? ` · ${campaignOf.get(p.path)?.name}` : ''}
                    </span>
                  </span>
                  {st.tag ? (
                    <span className="pg-rail-tag" style={{ color: st.color }}>
                      {st.tag}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div>
            <div className="pg-rail-head">ON EVERY PAGE</div>
            {(
              [
                [
                  'header',
                  'Header',
                  `${headerSummary(nav, hasAskPage(chatData?.readerChat))}${railNote(linkIssues, 'header')}`,
                ],
                ['footer', 'Footer', `${footerSummary(nav)}${railNote(linkIssues, 'footer')}`],
              ] as Array<[Part, string, string]>
            ).map(([id, title, sub]) => (
              <button
                key={id}
                type="button"
                className="pg-rail-item"
                data-active={sel === id ? 'true' : undefined}
                aria-current={sel === id ? 'page' : undefined}
                onClick={() => setSel(id)}
              >
                <span
                  className="pg-dot"
                  style={{
                    background: site?.state?.hasUnpublishedChanges
                      ? 'var(--ink-amber)'
                      : 'var(--ink-green)',
                  }}
                />
                <span className="pg-rail-text">
                  <span className="pg-rail-title">{title}</span>
                  <span
                    className="pg-rail-sub"
                    // v14: the line turns amber when its links go to empty sections.
                    style={
                      linkIssues.some((i) => i.part === id)
                        ? { color: 'var(--ink-amber)' }
                        : undefined
                    }
                  >
                    {sub}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {accountId ? (
            <RecentlyDeleted
              accountId={accountId}
              items={deleted}
              restoreMutation={RESTORE_TINYCMS_PAGE}
              onRestored={onRestored}
              note="Restored pages come back as drafts. Publish one again to put it back on your site."
              collapsible="tinycms.pages.deletedOpen"
            />
          ) : null}
        </aside>

        <div className="pg-main">
          {part ? (
            <PartPane
              part={part}
              site={site}
              draftPaths={draftPaths}
              onEdit={() => setEditing({ kind: part })}
              onChanged={() => void refetchSite()}
            />
          ) : page ? (
            <PagePane
              key={page.id}
              row={page}
              isHome={page.id === homeId}
              home={ordered.find((p) => p.id === homeId) || null}
              focus={fix && page.id === initial}
              focusBlock={page.id === initial ? block : null}
              siteUrl={site?.siteUrl || null}
              campaign={campaignOf.get(page.path) || null}
              onEdit={() => editPage(page)}
              onOpenFinding={(finding) => editPage(page, finding)}
              onChanged={() => void refetch()}
              onDeleted={() => {
                setSel(null);
                void refetch();
                void refetchDeleted();
              }}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Content → Pages when every page was deleted: what readers see at / right
 * now, and one button to bring them all back. They come back as drafts (the
 * server never republishes on restore), and the menu and home page setting
 * stay as they were, so the note says how to finish.
 */
function PagesGone({
  accountId,
  deleted,
  gap,
  onRestored,
}: {
  accountId: string;
  deleted: DeletedItem[];
  gap: HomeGap | null;
  onRestored: (id: string) => void;
}) {
  const { run, busy } = useRestoreAllPages(accountId);
  const [failed, setFailed] = useState<Array<{ title: string; message: string }>>([]);
  const n = deleted.length;

  const restoreAll = async () => {
    setFailed([]);
    const { restored, failed: refused } = await run(deleted);
    setFailed(refused);
    if (restored.length) {
      showInkToast(
        refused.length
          ? `Restored ${restored.length} of ${n} pages as drafts.`
          : `Restored ${plural(restored.length, 'page')} as drafts. Publish them to put them back on your site.`
      );
      onRestored(restored[0]);
    }
  };

  return (
    <div className="pg-empty" data-tone="gone">
      <div className="pg-empty-title">Your site has no pages</div>
      <p>
        {gap ? `Right now, anyone who opens your site sees ${readersSee(gap)}. ` : ''}
        {deletedSentence(deleted)}
      </p>
      <div className="pg-empty-actions">
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={busy}
          onClick={() => void restoreAll()}
        >
          {busy
            ? 'Restoring…'
            : n === 1
              ? `Restore “${deleted[0].title || 'Untitled'}”`
              : `Restore all ${n}`}
        </button>
      </div>
      {failed.length ? (
        <ul role="alert" className="pg-empty-errors">
          {failed.map((f, i) => (
            <li key={i}>
              {f.title}: {f.message}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="pg-note">
        Pages come back as drafts. Publish them to put them on your site again. The menu and home
        page are site settings: ask your agent to set them again, or restore an earlier version in
        Site.
      </p>
      <div style={{ marginTop: 20 }}>
        <RecentlyDeleted
          accountId={accountId}
          items={deleted}
          restoreMutation={RESTORE_TINYCMS_PAGE}
          onRestored={onRestored}
          note="Or restore them one at a time."
        />
      </div>
    </div>
  );
}

/**
 * The working copy of a page (or the whole site) in a frame, at desktop or
 * phone width. The preview link is minted once; the frame loads its page
 * with ?preview= so no cookie is needed (see smagent-sites middleware).
 */
export function WorkingPreview({
  pageId,
  articleId,
  path,
  outline,
  device,
  height = 760,
  focusBlock = null,
  pageCode = false,
  onReady,
  onFrameLoad,
}: {
  /** Pages run their own code in this preview (an admin's "Run page code"). */
  pageCode?: boolean;
  /**
   * The page editor (PageEditor.tsx): this preview's token, and a way to send
   * the page a message (refresh now, mark the block being edited).
   */
  onReady?: (preview: { token: string; href: string; post: (msg: object) => void }) => void;
  /** The frame finished loading (again): the editor re-marks the block it's on. */
  onFrameLoad?: () => void;
  pageId?: string;
  articleId?: string;
  /** Path to show instead of the one the link names (the home page lives at /). */
  path?: string;
  outline?: 'blocks' | Part | null;
  /** Scroll the page to this block (its index) and mark it (smagent-sites PreviewOutline ?focus=). */
  focusBlock?: string | null;
  device: Device;
  height?: number | string;
}) {
  const { accountId } = useAccount();
  const [create] = useMutation(CREATE_TINYCMS_PREVIEW);
  const [link, setLink] = useState<{
    origin: string;
    next: string;
    token: string;
    /** Anything else the link carries (?account= on a local site). */
    extra: [string, string][];
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    setLink(null);
    setError('');
    create({
      variables: {
        accountId,
        pageId: pageId || null,
        articleId: articleId || null,
        pageCode: pageCode || null,
      },
    })
      .then(({ data }) => {
        const url = data?.createTinycmsPreview?.url;
        if (!alive || !url) return;
        const u = new URL(url);
        const token = u.pathname.split('/preview/')[1] || '';
        const extra = [...u.searchParams.entries()].filter(([k]) => k !== 'next');
        setLink({ origin: u.origin, next: u.searchParams.get('next') || '/', token, extra });
      })
      .catch((err: Error) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [accountId, pageId, articleId, pageCode, create]);

  const { wrapRef, scale } = useFrameScale(device);
  // Labels in "Show blocks" stay readable when the desktop view is scaled down.
  const labelScale = scale < 1 ? Math.min(4, Math.round((1 / scale) * 2) / 2) : 1;

  const src = useMemo(() => {
    if (!link) return null;
    const u = new URL(path || link.next, link.origin);
    for (const [k, v] of link.extra) u.searchParams.set(k, v);
    u.searchParams.set('preview', link.token);
    u.searchParams.set('embed', '1');
    if (outline) u.searchParams.set('outline', outline);
    if (outline && labelScale > 1) u.searchParams.set('ui', String(labelScale));
    if (focusBlock && /^\d+$/.test(focusBlock)) u.searchParams.set('focus', focusBlock);
    return u.toString();
  }, [link, path, outline, labelScale, focusBlock]);

  const frameRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(onReady);
  readyRef.current = onReady;
  useEffect(() => {
    if (!link) return;
    // The same page full size, on this preview's link (no embed or outline).
    const full = new URL(path || link.next, link.origin);
    for (const [k, v] of link.extra) full.searchParams.set(k, v);
    full.searchParams.set('preview', link.token);
    readyRef.current?.({
      token: link.token,
      href: full.toString(),
      post: (msg) => frameRef.current?.contentWindow?.postMessage(msg, link.origin),
    });
  }, [link, path]);

  const frameStyle = frameStyleFor(scale, height);

  return (
    <div className="pg-stage">
      <div
        ref={wrapRef}
        className="pg-frame-wrap"
        data-device={device}
        // Sized while loading too, so the loader sits mid-frame and nothing jumps when the page arrives.
        style={scale < 1 || error || !src ? { height } : undefined}
      >
        {error ? (
          <p className="pg-frame-note">{error}</p>
        ) : src ? (
          <iframe
            ref={frameRef}
            title="Preview of the working copy"
            src={src}
            className="pg-frame"
            style={frameStyle}
            onLoad={onFrameLoad}
          />
        ) : (
          <Loader label="Loading the preview" className="pg-frame-note" />
        )}
      </div>
    </div>
  );
}

const PAGE_CAMPAIGNS = gql`
  query PageCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
      landingPageUrl
    }
  }
`;

function PagePane({
  row,
  isHome,
  focus = false,
  focusBlock = null,
  siteUrl,
  home = null,
  campaign = null,
  onEdit,
  onOpenFinding,
  onChanged,
  onDeleted,
}: {
  /** Opens the editor on this page (backlog 8ffdec3d). */
  onEdit: () => void;
  /** Opens it on a copy edit finding's words. */
  onOpenFinding: (f: Finding) => void;
  row: PageRow;
  isHome: boolean;
  /** The site's home page now, for Make this the home page. */
  home?: { id: string; title: string; path: string } | null;
  focus?: boolean;
  /** The block the preview scrolls to (a placeholder's Edit). */
  focusBlock?: string | null;
  siteUrl: string | null;
  /** The campaign this page is the landing page of. */
  campaign?: { id: string; name: string } | null;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  // "Your agent drafted this" only when the site has an agent (nothing records who did).
  const agentConnected = useAgentConnected();
  const { accountId } = useAccount();
  const [deletePage, { loading: deleting }] = useMutation(DELETE_TINYCMS_PAGE);
  const { isOwner } = useAddons();
  const [setHomePage] = useMutation(SET_TINYCMS_HOME_PAGE, {
    refetchQueries: ['GetTinycmsSite'],
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [historyOpen, setHistoryOpen] = useState(false);
  // "Run page code": an admin sees the page's own code at work in the previews.
  const codeState = usePageCode(row.id);
  const [runCode, setRunCode] = useState(false);
  const canRunCode = codeState.canRun;
  const [checksOpen, setChecksOpen] = useState(focus);
  // History's "Go to Checks": as coming from Today's Fix, it scrolls there.
  const [checksAsked, setChecksAsked] = useState(false);
  const st = pageStatus(row.state);
  const live = Boolean(row.state.live);
  // History's numbers, shown once: "Live · v6", "Changes pending · v7 · Live v6".
  const numbers = useSavedNumbers(
    'page',
    row.id,
    `${row.updatedAt}:${row.state.live?.version}:${row.state.hasUnpublishedChanges}`
  );
  const versionLine = pageVersionLine(
    st.label,
    numbers.active ? withSavedNumbers(row.state, numbers.numbers) : row.state
  );
  const path = isHome ? '/' : row.path;
  const takenDown = !live && row.state.takenDown ? row.state.takenDown : null;
  const waiting = !takenDown && (!live || row.state.hasUnpublishedChanges);
  // Made with New page in this visit: the person's own draft.
  const mine = !live && madeHere.has(row.id);
  // Saved in the editor by the person, not changed since (backlog 8ffdec3d).
  const ownEdit = Boolean((row.state as { ownEdit?: unknown }).ownEdit);
  const publishEdits = usePublishEdits();
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  const publishOwn = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      const r = await publishEdits({
        pageId: row.id,
        workingHash: row.workingHash,
        copyEditRequired: Boolean(
          (row.state as { copyEdit?: { required?: boolean } }).copyEdit?.required
        ),
      });
      if (r.ok) showInkToast(`${row.title} is live with your edits.`);
      else {
        setPublishError(`It can’t go live yet: ${r.message}.`);
        setChecksOpen(true);
      }
      onChanged();
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : 'That didn’t publish. Try again.');
    } finally {
      setPublishing(false);
    }
  };

  /** Undo goes back to the page that was home, or to none on a site that had none. */
  const moveHome = async (pageId: string | null) => {
    await setHomePage({ variables: { accountId, pageId } });
    onChanged();
  };
  const makeHome = async () => {
    const moves = [{ label: row.title, text: `${row.path} → /` }];
    if (home) {
      moves.push({ label: `${home.title}, the home page now`, text: `/ → ${home.path}` });
    }
    const ok = await askInk({
      title: `Make ${row.title} the home page?`,
      rows: moves,
      body: `Anyone who opens ${row.path} is sent to the home page, so old links keep working.${
        home ? ` ${home.title} stays live at its new address.` : ''
      } The change goes live when you confirm.`,
      confirmLabel: 'Make it the home page',
      run: {
        busyLabel: 'Moving…',
        failure: 'The home page didn’t change. Try again.',
        retryLabel: 'Try again',
        work: () => moveHome(row.id),
      },
    });
    if (!ok) return;
    showInkToast(`${row.title} is now the home page.`, {
      label: 'Undo',
      onClick: () =>
        void moveHome(home?.id || null).catch((err) =>
          showInkToast(err instanceof Error ? err.message : 'Couldn’t undo that.')
        ),
    });
  };

  const handleDelete = async () => {
    setDeleteError(null);
    const scheduled = Boolean(row.state.scheduled);
    const ok = await askInk({
      title: `Delete “${row.title}”?`,
      body:
        live || scheduled
          ? `It’s ${live ? 'live' : 'scheduled'} at ${row.path}. Deleting takes it off your site now.`
          : 'It isn’t on your site.',
      note: 'You can restore it from Recently deleted for 30 days.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deletePage({ variables: { accountId, id: row.id } });
      onDeleted();
    } catch (err) {
      // e.g. the header links to it, or it's the home page: the server says what to change.
      setDeleteError(err instanceof Error ? err.message : 'Couldn’t delete the page');
    }
  };

  const openChecks = () => {
    setChecksOpen(true);
    setTimeout(
      () => document.getElementById('pg-checks')?.scrollIntoView({ behavior: 'smooth' }),
      0
    );
  };

  return (
    <>
      <div className="pg-head">
        <div style={{ minWidth: 0 }}>
          <div className="pg-title">{row.title}</div>
          {campaign ? (
            <Link
              to={`/campaigns?campaign=${encodeURIComponent(campaign.id)}`}
              className="ink-quiet-link pg-campaign"
            >
              Landing page for {campaign.name} →
            </Link>
          ) : null}
          <div className="pg-meta">
            <span className="pg-status" style={{ color: st.color }}>
              <span className="pg-dot" style={{ background: st.dot }} />
              {versionLine}
            </span>
            <span>·</span>
            <span className="pg-url" data-muted={live && siteUrl ? undefined : 'true'}>
              {live && siteUrl
                ? `${siteUrl.replace(/^https?:\/\//, '')}${path === '/' ? '' : path}`
                : path}
            </span>
            {typeof row.blockCount === 'number' ? (
              <>
                <span>·</span>
                <span>
                  {row.blockCount} block{row.blockCount === 1 ? '' : 's'}
                  {row.updatedAt ? ` · ${ago(row.updatedAt)}` : ''}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="pg-tools">
          <button type="button" className="pg-history" onClick={() => setHistoryOpen(true)}>
            History
          </button>
          {canRunCode ? (
            <button
              type="button"
              className="pg-check"
              aria-pressed={runCode}
              title="Run this page’s own code in the previews (only you see this)"
              onClick={() => setRunCode((v) => !v)}
            >
              <span className="pg-box" data-on={runCode ? 'true' : undefined} />
              Run page code
            </button>
          ) : null}
          <DeviceSwitch device={device} onDevice={setDevice} />
          {/* One way out: the draft while there is one, otherwise the live page. */}
          {live && siteUrl && !waiting ? (
            <a
              href={`${siteUrl}${path === '/' ? '' : path}`}
              target="_blank"
              rel="noreferrer"
              className="ink-btn"
            >
              Open ↗
            </a>
          ) : (
            <PreviewButton pageId={row.id} label="Open" pageCode={canRunCode && runCode} />
          )}
          <button type="button" className="ink-btn ink-btn-primary" onClick={onEdit}>
            Edit
          </button>
          <PageMoreMenu
            isHome={isHome}
            isAdmin={isOwner}
            live={live}
            // A landing page has its own place; a child page or translation can't be at /.
            canBeHome={!campaign && row.path.split('/').length === 2}
            homeTitle={home?.title || null}
            deleting={deleting}
            onMakeHome={() => void makeHome()}
            onDelete={() => void handleDelete()}
          />
        </div>
      </div>

      {takenDown ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-line3)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">Taken down — not on your site</div>
            <div className="pg-action-note">
              {takenDown.by ? `${takenDown.by} took it down` : 'Taken down'}
              {takenDown.at ? ` ${ago(takenDown.at)}` : ''}. Delete it below, or publish it again.
            </div>
          </div>
          <button type="button" className="ink-btn" onClick={openChecks}>
            Publish again
          </button>
        </div>
      ) : null}

      {waiting && ownEdit && live ? (
        <OwnEditsCard busy={publishing} error={publishError} onPublish={() => void publishOwn()} />
      ) : waiting ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">
              {mine
                ? 'Your draft page'
                : agentConnected
                  ? live
                    ? 'Your agent changed this page'
                    : 'Your agent drafted this page'
                  : live
                    ? 'This page has changes not published yet'
                    : 'This page isn’t published yet'}
            </div>
            <div className="pg-action-note">
              {live
                ? 'Readers still see the live version until you publish.'
                : 'Readers can’t see it yet.'}
            </div>
          </div>
          <button type="button" className="ink-btn ink-btn-primary" onClick={openChecks}>
            {live ? 'Review and publish' : mine ? 'Publish page' : 'Review and publish page'}
          </button>
        </div>
      ) : null}

      <WorkingPreview
        pageId={row.id}
        path={path}
        device={device}
        outline={null}
        focusBlock={focusBlock}
        pageCode={canRunCode && runCode}
      />
      <p className="pg-note">
        Preview of the working copy. Edit it here, or ask your agent.
        {canRunCode && runCode ? ' This page’s own code is running in it.' : ''}
      </p>

      <div className="pg-more">
        <button
          type="button"
          className="pg-more-toggle"
          aria-expanded={checksOpen}
          onClick={() => setChecksOpen((v) => !v)}
        >
          {checksOpen ? '▾' : '▸'} Checks
        </button>
        {checksOpen ? (
          <PageChecks
            id={row.id}
            onChanged={onChanged}
            onOpenFinding={onOpenFinding}
            focus={focus || checksAsked}
          />
        ) : null}
      </div>

      <PageCodeSection pageId={row.id} live={live} />

      {historyOpen ? (
        <HistoryView
          kind="page"
          id={row.id}
          onClose={() => setHistoryOpen(false)}
          onChanged={onChanged}
          onOpenChecks={() => {
            setHistoryOpen(false);
            setChecksOpen(true);
            setChecksAsked(true);
          }}
        />
      ) : null}

      <div className="pg-delete">
        <button
          type="button"
          className="ink-btn"
          style={{ color: 'var(--ink-red)' }}
          disabled={deleting || isHome || !isOwner}
          title={isOwner ? undefined : ONLY_ADMINS}
          onClick={() => void handleDelete()}
        >
          {deleting ? 'Deleting…' : 'Delete page'}
        </button>
        {isHome ? (
          <span className="pg-note" style={{ margin: 0 }}>
            {HOME_CANT_DELETE}
          </span>
        ) : null}
        {deleteError ? (
          <span role="alert" style={{ color: 'var(--ink-red)', fontSize: 13 }}>
            {deleteError}
          </span>
        ) : null}
      </div>
    </>
  );
}

const ONLY_ADMINS = 'Only admins can do this.';
const HOME_CANT_DELETE =
  'The home page can’t be deleted. Make another page the home page first: open it, then ··· › Make this the home page.';

/** ··· after Edit (Round 44 §3): make this the home page, delete it. */
function PageMoreMenu({
  isHome,
  isAdmin,
  live,
  canBeHome,
  homeTitle,
  deleting,
  onMakeHome,
  onDelete,
}: {
  isHome: boolean;
  isAdmin: boolean;
  live: boolean;
  canBeHome: boolean;
  homeTitle: string | null;
  deleting: boolean;
  onMakeHome: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const choose = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  const homeSub = !isAdmin
    ? ONLY_ADMINS
    : !live
      ? 'Publish it first. Only a live page can be the home page.'
      : homeTitle
        ? `Replaces ${homeTitle} at /. You confirm first.`
        : 'Shows it at /. You confirm first.';
  const deleteSub = !isAdmin
    ? ONLY_ADMINS
    : isHome
      ? HOME_CANT_DELETE
      : 'It moves to Recently deleted. You can restore it for 30 days.';
  return (
    <span className="af-menu-wrap">
      <button
        type="button"
        className="ink-btn"
        aria-label="More for this page"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ···
      </button>
      {open ? (
        <>
          <span className="af-scrim" onClick={() => setOpen(false)} />
          <span className="af-menu" role="menu">
            {canBeHome && !isHome ? (
              <button
                type="button"
                role="menuitem"
                disabled={!isAdmin || !live}
                onClick={choose(onMakeHome)}
              >
                <span className="af-item-label">Make this the home page</span>
                <span className="af-item-sub">{homeSub}</span>
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              disabled={!isAdmin || isHome || deleting}
              onClick={choose(onDelete)}
            >
              <span className="af-item-label" data-danger={isHome ? undefined : 'true'}>
                Delete page
              </span>
              <span className="af-item-sub">{deleteSub}</span>
            </button>
          </span>
        </>
      ) : null}
    </span>
  );
}

/** Header or footer: the home page with that part picked out, and its links. */
function PartPane({
  part,
  site,
  draftPaths,
  onEdit,
  onChanged,
}: {
  part: Part;
  site: any;
  draftPaths: Set<string>;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [device, setDevice] = useState<Device>('desktop');
  const pending = Boolean(site?.state?.hasUnpublishedChanges);
  // The person saved the header or footer in the editor (backlog 8ffdec3d).
  const ownEdit = pending && Boolean(site?.state?.ownEdit) && Boolean(site?.state?.live);
  const publishEdits = usePublishEdits();
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  const publishOwn = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      const r = await publishEdits({ siteHash: site.workingHash });
      if (r.ok) showInkToast(`The ${part} is live with your edits.`);
      else setPublishError(`It can’t go live yet: ${r.message}.`);
      onChanged();
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : 'That didn’t publish. Try again.');
    } finally {
      setPublishing(false);
    }
  };
  const nav = site?.working?.navigation;
  return (
    <>
      <div className="pg-head">
        <div style={{ minWidth: 0 }}>
          <div className="pg-title">{part === 'header' ? 'Header' : 'Footer'}</div>
          <div className="pg-meta">
            <span
              className="pg-status"
              style={{ color: pending ? 'var(--ink-amber)' : 'var(--ink-green-ink)' }}
            >
              <span
                className="pg-dot"
                style={{ background: pending ? 'var(--ink-amber)' : 'var(--ink-green)' }}
              />
              {pending ? 'Changes pending' : 'Live'}
            </span>
            <span>·</span>
            <span>On every page</span>
          </div>
        </div>
        <div className="pg-tools">
          <DeviceSwitch device={device} onDevice={setDevice} />
          <PreviewButton label="Preview" />
          <button type="button" className="ink-btn ink-btn-primary" onClick={onEdit}>
            Edit
          </button>
        </div>
      </div>

      {ownEdit ? (
        <OwnEditsCard busy={publishing} error={publishError} onPublish={() => void publishOwn()} />
      ) : pending ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">The header and footer have changes</div>
            <div className="pg-action-note">
              They publish with your site’s theme. Readers see the live version until then.
            </div>
          </div>
          <Link to="/site#theme" className="ink-btn ink-btn-primary">
            Review in Site
          </Link>
        </div>
      ) : null}

      <MenuLinkChecks part={part} site={site} draftPaths={draftPaths} />

      <WorkingPreview path="/" device={device} outline={part} />
      <p className="pg-note">Preview of the working copy. Edit it here, or ask your agent.</p>

      <div className="pg-more">
        <CmsNavigation
          navigation={nav}
          homePage={site?.working?.layout?.homePage}
          showTagline={site?.working?.layout?.showTagline}
        />
      </div>
    </>
  );
}

/** After Save draft in the editor: the edits wait as a draft until published (backlog 8ffdec3d). */
function OwnEditsCard({
  busy,
  error,
  onPublish,
}: {
  busy: boolean;
  error: string;
  onPublish: () => void;
}) {
  return (
    <div className="pg-action">
      <span className="pg-dot" style={{ background: 'var(--ink-amber)', width: 8, height: 8 }} />
      <div className="pg-action-text">
        <div className="pg-action-title">Your edits are saved as a draft</div>
        <div className="pg-action-note">
          {error || 'Readers still see the live version until you publish.'}
        </div>
      </div>
      <button type="button" className="ink-btn ink-btn-primary" disabled={busy} onClick={onPublish}>
        {busy ? 'Publishing…' : 'Publish changes'}
      </button>
    </div>
  );
}

/**
 * Menu links that need attention, on Header / Footer ("tinycms UX
 * optimization review v13"): links to a section with no published articles
 * ("Hide until it has articles" / "Assign articles"), and links to an address
 * with nothing live (Remove link). Each change is to the working site
 * document, so it goes live with the theme's next publish, and can be undone.
 */
function MenuLinkChecks({
  part,
  site,
  draftPaths,
}: {
  part: Part;
  site: any;
  draftPaths: Set<string>;
}) {
  const { accountId } = useAccount();
  const [updateSite, { loading: saving }] = useMutation(UPDATE_TINYCMS_SITE, {
    refetchQueries: ['GetTinycmsSite'],
  });
  const [asking, setAsking] = useState<string | null>(null);
  const { copy, state } = useCopy();
  const nav = site?.working?.navigation;
  const issues = navIssues(site?.state?.findings, { draftPaths, hidden: true }).filter(
    (i) => i.part === part
  );
  if (!issues.length || !accountId) return null;
  const total = partLinkCount(nav, part);
  const empty = issues.filter((i) => i.kind === 'empty');
  const hiddenLinks = issues.filter((i) => i.kind === 'hidden');
  const notLive = issues.filter((i) => i.notLive);
  const missing = issues.filter((i) => i.kind === 'missing' && !i.notLive);

  /** Saves a navigation patch, with Undo putting back exactly the lists it changed. */
  const change = async (patch: Record<string, unknown>, toast: string) => {
    if (!Object.keys(patch).length) return;
    const before = Object.fromEntries(Object.keys(patch).map((k) => [k, nav?.[k] || []]));
    try {
      await updateSite({ variables: { accountId, input: { navigation: patch } } });
      showInkToast(toast, {
        label: 'Undo',
        onClick: () =>
          void updateSite({ variables: { accountId, input: { navigation: before } } }).catch(
            (err) => showInkToast(err instanceof Error ? err.message : 'Couldn’t undo that.')
          ),
      });
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save.');
    }
  };
  const hide = (i: NavIssue) =>
    change(
      hidePatch(nav, i.href, true),
      `${i.label} is hidden once you publish the theme. It comes back to the ${part} by itself when the section has an article.`
    );
  const remove = (i: NavIssue) =>
    change(
      removePatch(nav, i.href),
      `Removed “${i.label}” from the ${part}. Publish the theme to take it off your site.`
    );

  const rows = (list: NavIssue[]) =>
    list.map((i) => {
      const noArticles = i.kind === 'empty' || i.kind === 'hidden';
      const prompt = noArticles ? assignPrompt(i) : '';
      const copied = state(i.key);
      return (
        <div key={i.key} className="pg-links-row">
          <div className="pg-links-row-text">
            <div className="pg-links-row-label">{i.label}</div>
            <div className="pg-links-row-note">
              {i.href} ·{' '}
              {i.kind === 'hidden'
                ? 'No articles assigned yet · Hidden on your site'
                : i.kind === 'empty'
                  ? 'No articles assigned yet'
                  : i.notLive
                    ? 'The page isn’t live yet'
                    : 'No live page, article or section here'}
            </div>
            {asking === i.key ? (
              <div className="today-reschedule">
                <span className="ink-muted">
                  Ask your agent. Its picks come back to Today for your approval:
                </span>
                <div className="pg-agent-prompt">{prompt}</div>
              </div>
            ) : null}
          </div>
          <div className="pg-links-row-actions">
            {noArticles ? (
              <>
                {i.kind === 'empty' ? (
                  <button
                    type="button"
                    className="ink-btn"
                    onClick={() => void hide(i)}
                    disabled={saving}
                  >
                    Hide until it has articles
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ink-btn"
                    onClick={() => void remove(i)}
                    disabled={saving}
                  >
                    Remove link
                  </button>
                )}
                {asking === i.key ? (
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary"
                    onClick={(e) => void copy(i.key, prompt, e)}
                  >
                    {copied === 'copied'
                      ? 'Copied'
                      : copied === 'failed'
                        ? 'Couldn’t copy'
                        : 'Copy prompt'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary"
                    onClick={() => setAsking(i.key)}
                  >
                    Assign articles
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="ink-btn"
                onClick={() => void remove(i)}
                disabled={saving}
              >
                Remove link
              </button>
            )}
          </div>
        </div>
      );
    });

  const goes = (n: number, one: string, many: string) =>
    `${n} of ${plural(total, `${part} link`)} ${n === 1 ? `goes to ${one}` : `go to ${many}`}`;
  return (
    <>
      {empty.length ? (
        <div className="pg-links">
          <div className="pg-links-head">
            <span className="pg-dot" style={{ background: 'var(--ink-amber)' }} />
            <div>
              <div className="pg-action-title">
                {goes(empty.length, 'an empty section', 'empty sections')}
              </div>
              <div className="pg-action-note">
                Readers who click them see “Nothing published here yet”.
              </div>
            </div>
          </div>
          {rows(empty)}
        </div>
      ) : null}
      {hiddenLinks.length ? (
        <div className="pg-links">
          <div className="pg-links-head">
            <span className="pg-dot" style={{ background: 'var(--ink-amber)' }} />
            <div>
              <div className="pg-action-title">
                {goes(hiddenLinks.length, 'an empty section', 'empty sections')}
              </div>
              <div className="pg-action-note">
                Your site hides {hiddenLinks.length === 1 ? 'it' : 'them'} until{' '}
                {hiddenLinks.length === 1 ? 'that section has' : 'those sections have'} articles.
              </div>
            </div>
          </div>
          {rows(hiddenLinks)}
        </div>
      ) : null}
      {notLive.length ? (
        <div className="pg-links">
          <div className="pg-links-head">
            <span className="pg-dot" style={{ background: 'var(--ink-amber)' }} />
            <div>
              <div className="pg-action-title">
                {goes(notLive.length, 'a page that isn’t live yet', 'pages that aren’t live yet')}
              </div>
              <div className="pg-action-note">
                They go live together with the site: one approval in Site or on Today publishes the
                menus and the pages they link to.
              </div>
            </div>
            <Link to="/site#theme" className="ink-btn ink-btn-primary">
              Publish together
            </Link>
          </div>
          {rows(notLive)}
        </div>
      ) : null}
      {missing.length ? (
        <div className="pg-links">
          <div className="pg-links-head">
            <span className="pg-dot" style={{ background: 'var(--ink-red)' }} />
            <div>
              <div className="pg-action-title">
                {goes(missing.length, 'a page that doesn’t exist', 'pages that don’t exist')}
              </div>
              <div className="pg-action-note">
                Readers who click them find nothing there, so publishing the theme asks for a reason
                until they’re fixed.
              </div>
            </div>
          </div>
          {rows(missing)}
        </div>
      ) : null}
    </>
  );
}

/**
 * A page's checks, publish controls and approval history. Checks read in plain
 * words (the server's labels), and two fix in place: a form that isn't live
 * publishes with the page under one approval, and a missing share image is
 * picked from Media. `focus` marks the open checks (Today → Fix).
 */
function PageChecks({
  id,
  onChanged,
  onOpenFinding,
  focus = false,
}: {
  id: string;
  onChanged: () => void;
  onOpenFinding: (f: Finding) => void;
  focus?: boolean;
}) {
  const { accountId } = useAccount();
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_PAGE, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'network-only',
  });
  // Follow the agent's edits while the checks are open: refetch, and hold
  // Publish a moment so the version approved is the one on screen.
  const live = useLiveChanges(true, () => refetch());
  // History's numbers, not the publish count (savedNumbers.ts).
  const numbers = useSavedNumbers(
    'page',
    id,
    `${data?.tinycmsPage?.workingHash}:${data?.tinycmsPage?.state?.live?.version}:${data?.tinycmsPage?.state?.scheduled?.version}`
  );
  const [copyEdit, { loading: editing }] = useMutation(RUN_TINYCMS_PAGE_COPY_EDIT);
  const [publishWithForms, { loading: publishingForms }] = useMutation(PUBLISH_TINYCMS_PAGES);
  const [setShareImage, { loading: settingImage }] = useMutation(SET_TINYCMS_PAGE_SHARE_IMAGE);
  // What Today approves together with this page: pages it links to, their
  // forms, and the site's menus when they point at it (backlog bfbfa5d7).
  const today = useToday('day', { skip: !accountId });
  const { publishTogether, step: togetherStep } = usePublishTogether();
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState('');
  const page = data?.tinycmsPage;
  const loaded = Boolean(page);
  useEffect(() => {
    if (focus && loaded) {
      document.getElementById('pg-checks')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [focus, loaded]);
  if (loading && !page) return <Loader label="Loading" className="ink-site-row-desc" />;
  if (!page) return null;

  const state = numbers.active ? withSavedNumbers(page.state, numbers.numbers) : page.state;
  const preflight: Finding[] = state.preflight || [];
  const copy: Finding[] = state.copyEdit?.fresh ? state.copyEdit.findings || [] : [];
  const notRun = !state.copyEdit?.fresh;
  const findings = [
    ...preflight,
    ...(state.copyEdit?.required
      ? copy
      : copy.map((f) => ({ ...f, severity: 'warning' as const }))),
  ];
  const copyEditMissing = notRun && state.copyEdit?.required ? 1 : 0;
  const blocking = findings.filter((f) => f.severity === 'blocking').length + copyEditMissing;
  const forms = formsToPublish(findings);
  const formsBlocked = otherBlocking(findings, copyEditMissing) > 0;
  const group = today.data?.groups?.find((g) => g.pages.includes(id));
  // Offered while this page is held up by what it goes live with. Something
  // else in the bundle still to fix: say what, and wait.
  const together =
    group && blocking > 0 && group.pages.length + (group.site ? 1 : 0) > 1
      ? bundleItems(group, today.data?.waiting || [])
      : null;
  const togetherBlocked = bundleBlockedLine(group);
  const others = (together || []).filter((i) => i.id !== id && i.kind === 'page');
  const withSite = Boolean(together?.some((i) => i.kind === 'site'));
  const withForms = (together || []).filter((i) => i.kind === 'form').length;

  const publishAll = async () => {
    if (!together) return;
    setError('');
    const r = await publishTogether(together);
    if (r.ok) showInkToast(`Published “${page.title}” and ${together.length - 1} more.`);
    else setError(r.message);
    await today.refetch();
    done();
  };

  const done = () => {
    void refetch();
    onChanged();
  };

  const publishForms = async () => {
    setError('');
    const what = formsPhrase(forms);
    if (
      !(await askInk({
        title: `Publish “${page.title}” (${versionName(state)}) and ${what} now?`,
        body: 'They go live together. Your approval is recorded with your name and the time.',
        confirmLabel: 'Publish',
      }))
    )
      return;
    try {
      // The form versions approved are the ones the server has now.
      const versions = await Promise.all(
        forms.map(async (f) => {
          const { data: fd } = await client.query({
            query: GET_TINYCMS_FORM,
            variables: { accountId, id: f.id },
            fetchPolicy: 'network-only',
          });
          return { formId: f.id, contentHash: fd?.tinycmsForm?.workingHash as string };
        })
      );
      const { data: res } = await publishWithForms({
        variables: {
          accountId,
          pages: [{ pageId: id, contentHash: page.workingHash }],
          forms: versions,
        },
      });
      const r = res?.publishTinycmsPages;
      if (r?.ok) {
        showInkToast(`Published “${page.title}” and ${what}.`);
      } else {
        const bad = (r?.items || []).find((i: any) => i.ok === false);
        const own =
          forms.length === 1
            ? 'has its own checks to fix first. Open it in Forms.'
            : 'have their own checks to fix first. Open them in Forms.';
        setError(
          bad?.kind === 'form'
            ? `Nothing was published: ${what} ${own}`
            : `Nothing was published: ${bad?.message || r?.message || 'refused'}`
        );
      }
      done();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const pickShareImage = async (url: string) => {
    setPicking(false);
    setError('');
    try {
      await setShareImage({ variables: { accountId, id, url } });
      showInkToast('Share image added. Publish the page to use it.');
      done();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fix = (f: Finding) => {
    if (f.code === 'starter_text' || f.code === 'empty_article_list') {
      return (
        <button
          type="button"
          className="ink-link-btn ink-finding-fix"
          onClick={() => onOpenFinding(f)}
        >
          Go to block
        </button>
      );
    }
    if (f.source === 'copy_edit' && f.quote) {
      return (
        <button
          type="button"
          className="ink-link-btn ink-finding-fix"
          onClick={() => onOpenFinding(f)}
        >
          Go to text
        </button>
      );
    }
    const kind = checkFix(f);
    if (kind === 'publish-form') {
      return (
        <button
          type="button"
          className="ink-link-btn ink-finding-fix"
          onClick={() => void publishForms()}
          disabled={publishingForms || formsBlocked || live.justChanged}
          title={formsBlocked ? 'Fix the other blocking checks first' : undefined}
        >
          {publishingForms
            ? 'Publishing…'
            : forms.length > 1
              ? 'Publish the forms with this page'
              : 'Publish the form with this page'}
        </button>
      );
    }
    if (kind === 'share-image') {
      return (
        <button
          type="button"
          className="ink-link-btn ink-finding-fix"
          onClick={() => setPicking(true)}
          disabled={settingImage}
        >
          {settingImage ? 'Adding…' : 'Add one'}
        </button>
      );
    }
    return null;
  };

  const runCopyEdit = async () => {
    setError('');
    try {
      await copyEdit({ variables: { accountId, id } });
      await refetch();
      onChanged();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div id="pg-checks" className="pg-checks">
      <div>
        {notRun ? (
          <p className="ink-site-row-desc" style={{ marginBottom: 8 }}>
            The copy edit hasn’t run on this version
            {state.copyEdit?.required ? '. It’s needed before you can publish' : ''}.
          </p>
        ) : null}
        <FindingsList findings={findings} fix={fix} focus={focus} />
        {picking && accountId ? (
          <MediaUrlPicker
            accountId={accountId}
            note="Shown when someone shares this page. Publish the page to use it."
            onPick={(url) => void pickShareImage(url)}
            onClose={() => setPicking(false)}
          />
        ) : null}
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            onClick={() => void runCopyEdit()}
            disabled={editing}
          >
            {editing ? 'Checking…' : 'Run copy edit'}
          </button>
        </div>
        {error ? (
          <p className="text-xs text-error" style={{ marginTop: 6 }}>
            {error}
          </p>
        ) : null}
      </div>

      {together ? (
        <div className="pg-together">
          <p className="ink-site-kicker">Goes live together</p>
          <p className="ink-site-row-desc">
            {`It goes live with ${andList([
              ...others.map((o) => o.title || 'Untitled page'),
              ...(withSite ? ['the site’s header and footer'] : []),
              ...(withForms ? [withForms === 1 ? 'a form' : `${withForms} forms`] : []),
            ])}. They link to each other, so none of them can go live alone.`}
          </p>
          {togetherBlocked ? (
            <p className="ink-site-row-desc" style={{ color: 'var(--ink-red)', marginTop: 6 }}>
              {togetherBlocked}
            </p>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            style={{ marginTop: 8 }}
            onClick={() => void publishAll()}
            disabled={Boolean(togetherStep) || Boolean(togetherBlocked) || live.justChanged}
          >
            {togetherStep === 'checking'
              ? 'Checking the wording…'
              : togetherStep
                ? 'Publishing…'
                : `Publish all ${together.length}`}
          </button>
        </div>
      ) : null}

      <div>
        <p className="ink-site-kicker">Publish</p>
        <PublishPanel
          kind="page"
          id={id}
          workingHash={page.workingHash}
          state={state}
          blocking={blocking}
          heldFindings={findings.filter((f) => f.held)}
          hideState
          title={page.title}
          held={live.justChanged}
          onDone={done}
        />
      </div>
    </div>
  );
}

/**
 * An article's working copy, full screen ("tinycms UX optimization review
 * v5.zip", Prototype v3 → article Preview): the site's theme at desktop or
 * phone width, a link to open it in a tab, and ✕ or Escape to close.
 */
export function ArticlePreview({
  articleId,
  title,
  onClose,
}: {
  articleId: string;
  title: string;
  onClose: () => void;
}) {
  const [device, setDevice] = useState<Device>('desktop');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div className="pv-overlay" role="dialog" aria-modal="true" aria-label="Preview">
      <div className="pv-bar">
        <span className="pv-bar-title">Preview</span>
        <span className="pv-bar-sub">{title}</span>
        <span className="pv-bar-tools">
          <DeviceSwitch device={device} onDevice={setDevice} />
          <PreviewButton articleId={articleId} label="Open in new tab" />
          <button
            type="button"
            className="pv-close"
            title="Close (Esc)"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </span>
      </div>
      <div className="pv-body">
        <WorkingPreview articleId={articleId} device={device} height="calc(100vh - 160px)" />
        <p className="pg-note">
          Working copy, in your site’s theme. Readers see the published version.
        </p>
      </div>
    </div>
  );
}
